import { useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import MovieCard from '@/components/base/MovieCard';
import { movies, classicMovies } from '@/mocks/movies';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const numericId = Number(id);

  const movie = useMemo(() => movies.find((m) => m.id === numericId), [numericId]);
  const article = useMemo(() => articles.find((a) => a.id === numericId), [numericId]);

  const isMovie = !!movie;

  if (isMovie && movie) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-20 pb-12">
          <div className="mx-auto max-w-[1440px] px-4 md:px-6">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm mb-6 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="text-foreground-400 hover:text-foreground-200 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-foreground-500" />
              <Link to={`/category/${movie.categorySlug}`} className="text-foreground-400 hover:text-foreground-200 transition-colors">{movie.category}</Link>
              <i className="ri-arrow-right-s-line text-foreground-500" />
              <span className="text-foreground-200 font-medium">{movie.title}</span>
            </nav>

            {/* Hero */}
            <div className="relative rounded-xl overflow-hidden mb-10 h-[300px] md:h-[450px]">
              <img src={movie.banner} alt={movie.title} className="w-full h-full object-cover object-top" />
              <div className="absolute inset-0 bg-gradient-to-t from-background-950/90 via-background-950/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
                <div className="flex flex-col md:flex-row md:items-end gap-6">
                  <div className="w-32 md:w-40 flex-shrink-0 rounded-lg overflow-hidden shadow-2xl">
                    <img src={movie.poster} alt={movie.title} className="w-full aspect-[2/3] object-cover" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      {movie.tags.map((tag) => (
                        <span key={tag} className="px-2 py-0.5 rounded-full border border-foreground-300/30 text-foreground-300 text-xs">{tag}</span>
                      ))}
                    </div>
                    <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-50 mb-2">{movie.title}</h1>
                    <p className="text-sm text-foreground-300 mb-3">{movie.titleEn}</p>
                    <div className="flex items-center gap-4 text-sm text-foreground-300 flex-wrap">
                      <span className="flex items-center gap-1"><i className="ri-star-fill text-accent-500" /><span className="font-semibold text-accent-400">{movie.rating}</span></span>
                      <span>{movie.year}</span>
                      <span>{movie.duration}</span>
                      <span>{movie.region}</span>
                      <span>{movie.category}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Content */}
              <div className="lg:col-span-2">
                <div className="prose prose-invert max-w-none">
                  <div className="text-sm md:text-base text-foreground-200 leading-relaxed space-y-4" dangerouslySetInnerHTML={{ __html: movie.contentHtml }} />
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                <div className="rounded-xl bg-background-100/60 p-5">
                  <h3 className="font-heading text-base font-semibold text-foreground-100 mb-4">基本信息</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-foreground-400">导演</span>
                      <span className="text-foreground-200">{movie.director}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-foreground-400">主演</span>
                      <span className="text-foreground-200 text-right">{movie.cast.join(' / ')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-foreground-400">上映日期</span>
                      <span className="text-foreground-200">{movie.releaseDate}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-foreground-400">片长</span>
                      <span className="text-foreground-200">{movie.duration}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-foreground-400">地区</span>
                      <span className="text-foreground-200">{movie.region}</span>
                    </div>
                  </div>
                </div>

                {/* Related Movies */}
                <div>
                  <h3 className="font-heading text-base font-semibold text-foreground-100 mb-4">经典推荐</h3>
                  <div className="flex flex-col gap-2">
                    {classicMovies.slice(0, 4).map((m) => (
                      <MovieCard key={m.id} movie={m} variant="horizontal" />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
        <Footer />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Movie',
              name: movie.title,
              image: movie.poster,
              description: movie.description,
              dateCreated: movie.releaseDate,
              director: { '@type': 'Person', name: movie.director },
              actor: movie.cast.map((name) => ({ '@type': 'Person', name })),
              aggregateRating: { '@type': 'AggregateRating', ratingValue: movie.rating, bestRating: 10 },
            }),
          }}
        />
      </>
    );
  }

  if (article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-20 pb-12">
          <div className="mx-auto max-w-[1440px] px-4 md:px-6">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm mb-6 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="text-foreground-400 hover:text-foreground-200 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-foreground-500" />
              <span className="text-foreground-200 font-medium">影视资讯</span>
            </nav>

            <article className="max-w-4xl mx-auto">
              <div className="rounded-xl overflow-hidden mb-8 h-[300px] md:h-[400px]">
                <img src={article.image} alt={article.title} className="w-full h-full object-cover" />
              </div>

              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <span className="px-3 py-1 rounded-full bg-secondary-500/20 text-secondary-400 text-xs font-medium">{article.category}</span>
                {article.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full bg-background-200 text-foreground-400 text-xs">{tag}</span>
                ))}
              </div>

              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-100 mb-3">{article.title}</h1>
              <div className="flex items-center gap-4 text-sm text-foreground-400 mb-8">
                <span>{article.author}</span>
                <span>{article.publishDate}</span>
              </div>

              <div className="prose prose-invert max-w-none">
                <div className="text-sm md:text-base text-foreground-200 leading-relaxed space-y-4" dangerouslySetInnerHTML={{ __html: article.contentHtml }} />
              </div>
            </article>
          </div>
        </main>
        <Footer />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: article.title,
              image: article.image,
              datePublished: article.publishDate,
              author: { '@type': 'Organization', name: article.author },
              description: article.summary,
            }),
          }}
        />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="pt-20 pb-12">
        <div className="mx-auto max-w-[1440px] px-4 md:px-6 text-center py-20">
          <h1 className="font-heading text-2xl font-bold text-foreground-100 mb-4">内容未找到</h1>
          <Link to="/" className="text-primary-400 hover:text-primary-300 transition-colors">返回首页</Link>
        </div>
      </main>
      <Footer />
    </>
  );
}